import { LabelTooltipBackground } from 'features/mapping/icons';
import { FONT_NAMES } from 'features/mapping/styles/constants';
import { LayerProps } from 'shared/map-exports';
import { LocationTypes } from '../../../../../constants';

const ICONS_MIN_ZOOM = 8; // ~state level
const LABELS_MIN_ZOOM = 12; // ~city/district level

export const getLegacyIconStyle = (selectedGeoEventId: number): LayerProps => ({
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-allow-overlap': true,
    'icon-anchor': 'bottom',
    'icon-ignore-placement': false,
    'icon-size': [
      'interpolate',
      ['linear'],
      ['zoom'],
      ICONS_MIN_ZOOM,
      0.85,
      10,
      1,
    ],
    'icon-offset': [
      ['get', 'offsetX'],
      ['get', 'offsetY'],
    ],
    // For some reason, the icons need to be sorted by 'index',
    // but the labels by 'indexInv', in order to be sorted correctly
    // in both cases.
    'symbol-sort-key': ['get', 'index'],
    'symbol-z-order': 'source',
  },
  paint: {
    'icon-opacity': [
      'step',
      ['zoom'],
      [
        // Default opacity when zoom is less than ICONS_ZOOM
        'case',
        ['==', ['get', 'id'], selectedGeoEventId],
        1, // Opacity when 'id' is equal to 'selectedGeoEventId'
        0, // Opacity when 'id' is not equal to 'selectedGeoEventId'
      ],
      ICONS_MIN_ZOOM, // Zoom level at which opacity changes
      ['get', 'opacity'], // Opacity when zoom is greater than or equal to ICONS_ZOOM
    ],
  },
});

const legacyLabelStyleCommon: LayerProps = {
  type: 'symbol',
  layout: {
    'text-font': FONT_NAMES.regular,
    'text-field': [
      'format',
      ['get', 'name'],
      { 'text-font': ['literal', FONT_NAMES.bold] },
    ],
    'text-anchor': 'top',
    'text-justify': 'auto',
    'symbol-sort-key': ['get', 'indexInv'],
    'symbol-z-order': 'source',
  },
  paint: {
    'text-halo-width': 1.8,
  },
};

const labelStyleLight: LayerProps = {
  ...legacyLabelStyleCommon,
  paint: {
    ...legacyLabelStyleCommon.paint,
    'text-color': '#333333',
    'text-halo-color': 'rgba(255,255,255,0.8)',
  },
};

const labelStyleDark: LayerProps = {
  ...legacyLabelStyleCommon,
  paint: {
    ...legacyLabelStyleCommon.paint,
    'text-color': '#ffffff',
    'text-halo-color': 'rgba(0,0,0,0.8)',
  },
};

export const getLegacyLabelStyle = (
  lightBaseLayer: boolean,
  selectedGeoEventId: number,
): LayerProps => {
  const { layout, ...style } = lightBaseLayer
    ? labelStyleLight
    : labelStyleDark;
  return {
    ...style,
    minzoom: LABELS_MIN_ZOOM,
    layout: {
      ...layout,
      'symbol-sort-key': [
        // ensure it is always on top if it is selected
        'case',
        ['==', ['get', 'id'], selectedGeoEventId],
        -99999,
        ['get', 'indexInv'],
      ],
    },
    paint: {
      ...style.paint,
    },
  };
};

// Locations V2

export const getIconStyle = (selectedGeoEventId: number): LayerProps => ({
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-allow-overlap': true,
    'icon-anchor': 'bottom',
    'icon-ignore-placement': false,
    'icon-size': [
      'interpolate',
      ['linear'],
      ['zoom'],
      ICONS_MIN_ZOOM,
      0.85,
      10,
      1,
      13,
      1.25,
    ],
    'icon-offset': [
      ['get', 'offsetX'],
      ['get', 'offsetY'],
    ],
    // For some reason, the icons need to be sorted by 'index',
    // but the labels by 'indexInv', in order to be sorted correctly
    // in both cases.
    'symbol-sort-key': ['get', 'index'],
    'symbol-z-order': 'source',
  },
  paint: {
    'icon-opacity': [
      'step',
      ['zoom'],
      [
        // Default opacity when zoom is less than ICONS_ZOOM
        'case',
        ['==', ['get', 'id'], selectedGeoEventId],
        1, // Opacity when 'id' is equal to 'selectedGeoEventId'
        0, // Opacity when 'id' is not equal to 'selectedGeoEventId'
      ],
      ICONS_MIN_ZOOM, // Zoom level at which opacity changes
      ['get', 'opacity'], // Opacity when zoom is greater than or equal to ICONS_ZOOM
    ],
  },
});

const labelStyleCommon: LayerProps = {
  type: 'symbol',
  layout: {
    'text-font': FONT_NAMES.regular,
    'text-field': [
      'format',
      ['get', 'label'],
      { 'text-font': ['literal', FONT_NAMES.bold] },
    ],
    'text-anchor': 'top',
    'text-justify': 'auto',
    'symbol-sort-key': ['get', 'indexInv'],
    'symbol-z-order': 'source',
    'text-size': 12,
    'text-transform': 'uppercase',
    'text-offset': [0, 0.75],
    'icon-image': LabelTooltipBackground.name,
    'icon-text-fit': 'both',
    'text-max-width': 9,
    'icon-text-fit-padding': [4, 6, 4, 6], // [top, right, bottom, left]
  },
  paint: {
    'text-halo-width': 1.8,
    'icon-color': ['get', 'labelBackgroundColor'],
    'text-color': ['get', 'textColor'],
  },
};

export const getLabelStyle = (selectedGeoEventId: number): LayerProps => {
  return {
    ...labelStyleCommon,
    minzoom: LABELS_MIN_ZOOM,
    layout: {
      ...labelStyleCommon.layout,
      'symbol-sort-key': [
        // ensure it is always on top if it is selected
        'case',
        ['==', ['get', 'id'], selectedGeoEventId],
        -99999,
        ['get', 'indexInv'],
      ],
    },
    paint: {
      ...labelStyleCommon.paint,
    },
    // All other location types except general
    filter: ['!=', ['get', 'type'], LocationTypes.general],
  };
};
